import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import * as PermissionActions from './permission.actions';
import * as RoleActions from '../role-crud-state/role-crud.actions';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { PermissionControllerService } from '@rappider/rappider-sdk';
import { NotificationService } from 'libs/components/src/lib/services';
import { selectAdminPermissions } from './permission.selectors';

@Injectable()
export class PermissionEffects {
  constructor(
    private actions$: Actions,
    private permissionController: PermissionControllerService,
    private notificationService: NotificationService,
    private store: Store<any>
  ) { }

  getPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PermissionActions.GetPermissions,
        RoleActions.GetRoles
      ),
      switchMap(() => this.permissionController.find().pipe(
        map((permissions) => PermissionActions.GetPermissionsSuccessful({ permissions: permissions })),
        catchError((error) =>
          [
            PermissionActions.GetPermissionsFailure({ error: error, key: 'GetPermissions', timestamp: Date.now() })
          ]
        )
      )
      )
    )
  );

  getAdminPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionActions.GetAdminPermissions),
      switchMap(() => this.permissionController.findByAdmin().pipe(
        map((permissions) => PermissionActions.GetAdminPermissionsSuccessful({ permissions: permissions })),
        catchError((error) =>
          [
            PermissionActions.GetAdminPermissionsFailure({ error: error, key: 'GetAdminPermissions', timestamp: Date.now() })
          ]
        )
      )
      )
    )
  );

  createPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionActions.CreatePermission),
      switchMap((action) => this.permissionController.createByAdmin({ body: action.permission }).pipe(
        map((createdPermission) => {
          this.notificationService.createNotification(
            'success',
            createdPermission.title || 'Permission',
            'Permission Created Successfully'
          );
          return PermissionActions.CreatePermissionSuccessful({ permission: createdPermission });
        }),
        catchError((error) => {
          this.notificationService.createNotification(
            'error',
            action.permission.title || 'Permission',
            'Permission Could not Created'
          );
          return [
            PermissionActions.CreatePermissionFailure({ error: error, key: 'CreatePermission', timestamp: Date.now() })
          ];
        })
      ))
    )
  );

  updatePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionActions.UpdatePermission),
      switchMap((action) => this.permissionController.updateByIdByAdmin({ id: action.id, body: action.permission }).pipe(
        map(() => {
          this.notificationService.createNotification(
            'success',
            action.permission.title || 'Permission',
            'Permission Updated Successfully'
          );
          return PermissionActions.UpdatePermissionSuccessful({ id: action.id, permission: action.permission });
        }),
        catchError((error) => {
          this.notificationService.createNotification(
            'error',
            action.permission.title || 'Permission',
            'Permission Could not Updated'
          );
          return [
            PermissionActions.UpdatePermissionFailure({ error: error, key: 'UpdatePermission', timestamp: Date.now() })
          ];
        })
      ))
    )
  );

  deletePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionActions.DeletePermission),
      withLatestFrom(this.store.select(selectAdminPermissions)),
      switchMap(([action, permissions]) => {
        const permissionToDelete = permissions?.find(p => p.id === action.id);
        const permissionTitle = permissionToDelete?.title || 'Permission';

        return this.permissionController.deleteByIdByAdmin({ id: action.id }).pipe(
          map(() => {
            this.notificationService.createNotification(
              'success',
              permissionTitle,
              'Permission Deleted Successfully'
            );
            return PermissionActions.DeletePermissionSuccessful({ id: action.id });
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              permissionTitle,
              'Permission Could not Deleted'
            );
            return [
              PermissionActions.DeletePermissionFailure({ error: error, key: 'DeletePermission', timestamp: Date.now() })
            ];
          })
        );
      })
    )
  );

  refreshAdminPermissionsAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionActions.DeletePermissionSuccessful),
      map(() => PermissionActions.GetAdminPermissions())
    )
  );

  refreshAdminPermissionsAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionActions.CreatePermissionSuccessful),
      map(() => PermissionActions.GetAdminPermissions())
    )
  );

  refreshAdminPermissionsAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PermissionActions.UpdatePermissionSuccessful),
      map(() => PermissionActions.GetAdminPermissions())
    )
  );
}
